import api from './api';

const BASE_PATH = '/class_plans';

/**
 * Serviço de integração com a API do Plano de Aula Inteligente.
 * Respostas seguem JSON:API simplificado (FastJsonapi), com chaves em
 * dash-case dentro de data.attributes — por isso o parse manual abaixo,
 * no mesmo padrão já usado em services/slideDecks.js.
 */

function parseSchedule(schedule = {}) {
  return {
    weekday: schedule.weekday,
    startTime: schedule['start-time'],
    endTime: schedule['end-time'],
    teacherName: schedule['teacher-name']
  };
}

function parseSkill(skill = {}) {
  return {
    code: skill.code,
    description: skill.description
  };
}

// A API usa "proxima_aula", mas os componentes existentes (lesson-plan-smart-lesson-card)
// já usam as chaves de LESSON_STATUS ('realizada' | 'proxima' | 'prevista') vindas do mock.
// Mapeamos aqui pra não precisar tocar nesses componentes por causa do nome do enum.
const API_STATUS_TO_LESSON_STATUS = {
  realizada: 'realizada',
  proxima_aula: 'proxima',
  prevista: 'prevista'
};

export function mapLessonStatus(status) {
  return API_STATUS_TO_LESSON_STATUS[status] || status;
}

/**
 * Converte um item de class-plan (listagem, show ou resposta do favoritar)
 * pro formato camelCase usado no front.
 */
export function parseClassPlan(resource) {
  if (!resource) return null;
  const attributes = resource.attributes || {};

  return {
    id: String(resource.id ?? attributes.id),
    title: attributes.title,
    shift: attributes.shift,
    quarter: attributes.quarter,
    file: attributes.file,
    educationalStageId: attributes['educational-stage-id'],
    componentName: attributes['curricular-component'],
    periodStartDate: attributes['period-start-date'],
    periodEndDate: attributes['period-end-date'],
    skills: (attributes['bncc-skills'] || []).map(parseSkill),
    schedules: (attributes['schedules'] || []).map(parseSchedule),
    lessonsCompletedCount: attributes['lessons-completed-count'],
    lessonsTotalCount: attributes['lessons-total-count'],
    currentLessonNumber: attributes['current-lesson-number'],
    progressPercentage: attributes['progress-percentage'],
    favorited: !!attributes.favorited
  };
}

function parseLesson(lesson = {}) {
  return {
    id: lesson.id,
    date: lesson.date,
    startTime: lesson['start-time'],
    endTime: lesson['end-time'],
    teacherName: lesson['teacher-name'],
    status: mapLessonStatus(lesson.status),
    lessonNumber: lesson['lesson-number'],
    objectOfKnowledge: lesson['object-of-knowledge'],
    learningObjectives: lesson['learning-objectives'] || [],
    skills: (lesson['bncc-skills'] || []).map(parseSkill)
  };
}

function parseClassPlanLessons(doc) {
  const resource = doc?.data;
  if (!resource) return [];
  const attributes = resource.attributes || {};
  const monthsRaw = attributes['lessons-by-month'] || [];

  return monthsRaw.map(month => ({
    name: month.month,
    lessons: (month.lessons || []).map(parseLesson)
  }));
}

function parseRelatedLesson(item) {
  const attributes = item.attributes || {};
  return {
    id: item.id,
    entityId: attributes['entity-id'],
    entityType: attributes['entity-type'],
    path: attributes.path,
    translatedEntityType: attributes['translated-entity-type'],
    content: attributes.content,
    updatedAt: attributes['updated-at']
  };
}

function toClassPlanList(doc) {
  const data = doc?.data;
  if (!data) return [];
  return (Array.isArray(data) ? data : [data]).map(parseClassPlan);
}

/**
 * Monta os query params aceitos por GET /class_plans (seção 1 do doc).
 * São parâmetros "soltos" (snake_case), não filter[...] — diferente do
 * padrão JSON:API usado em outros endpoints da plataforma.
 */
function buildListParams({ q, year, quarter, shift, curricularComponent, favorite } = {}) {
  const params = {};
  if (q) params.q = q;
  if (year) params.year = year;
  if (quarter) params.quarter = quarter;
  if (shift) params.shift = shift;
  if (curricularComponent) params.curricular_component = curricularComponent;
  if (favorite) params.favorite = 'true';
  return params;
}

export const classPlansApi = {
  // GET /class_plans — listagem com filtros (cards fechados).
  list: async (filters = {}) => toClassPlanList(await api.get(BASE_PATH, { params: buildListParams(filters) })),

  // GET /class_plans/filters — opções pra popular os selects de filtro.
  filters: async () => {
    const doc = await api.get(`${BASE_PATH}/filters`);
    return {
      years: doc?.years || [],
      quarters: doc?.quarters || [],
      shifts: doc?.shifts || [],
      curricularComponents: doc?.['curricular-components'] || []
    };
  },

  // GET /class_plans/:id — card fechado avulso.
  show: async id => parseClassPlan((await api.get(`${BASE_PATH}/${id}`))?.data),

  // GET /class_plans/:id/lessons — aulas agrupadas por mês (card expandido, sob demanda).
  lessons: async id => parseClassPlanLessons(await api.get(`${BASE_PATH}/${id}/lessons`)),

  // PATCH /class_plans/:id — favoritar/desfavoritar.
  toggleFavorite: async (id, favorite) =>
    parseClassPlan(
      (
        await api.patch(`${BASE_PATH}/${id}`, {
          data: { type: 'class_plan', attributes: { favorite } }
        })
      )?.data
    )
};

export const classPlanLessonsApi = {
  // GET /class_plan_lessons/:id/related_lessons — aulas relacionadas (sob demanda).
  relatedLessons: async lessonId => {
    const doc = await api.get(`/class_plan_lessons/${lessonId}/related_lessons`);
    return (doc?.data || []).map(parseRelatedLesson);
  }
};

export default classPlansApi;
